import React, { useImperativeHandle, useState, forwardRef } from 'react';

import { FeatureFlag } from 'constants-flags';
import { useFlag } from 'lane-shared/hooks';

import { datadogRum } from '@datadog/browser-rum';
import cx from 'classnames';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module 'react-places-autocomplete'. Remove this comment to see the full error message
import XPlacesAutocomplete from 'react-places-autocomplete';

import { AddressType } from 'lane-shared/types/AddressType';

import useModalPosition, { ModalPositionEnum } from 'hooks/useModalPosition';

import { buildAddress } from './geocodeAddress';

import styles from './PlacesAutocomplete.scss';

export type PlacesAutocompleteHandle = {
  focus: () => void;
  blur: () => void;
  click: () => void;
};

type Props = {
  style?: React.CSSProperties;
  className?: string;
  value: string;
  placeholder?: string;
  onChange: (address: AddressType) => void;
  disabled?: boolean;
};

const PlacesAutocomplete = forwardRef<PlacesAutocompleteHandle, Props>(
  ({ value, placeholder, disabled, onChange, style, className }, ref) => {
    const [search, setSearch] = useState(value);
    const {
      buttonRef,
      modalRef,
      childrenRef,
      onOpen,
      position,
    } = useModalPosition({
      autoFocus: false,
      align: ModalPositionEnum.Dropdown,
    });
    const { t } = useTranslation();

    placeholder = t(
      'web.admin.content.metatag.editComplexProperties.addItemModal.address.searchPlaces'
    );

    useImperativeHandle(ref, () => ({
      focus: () => {
        if (childrenRef.current) {
          // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
          childrenRef.current.focus();
        }
      },
      blur: () => {
        if (childrenRef.current) {
          // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
          childrenRef.current.blur();
        }
      },
      click: () => {
        if (childrenRef.current) {
          // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
          childrenRef.current.click();
        }
      },
    }));

    const isMapsModuleFixEnabled = useFlag(FeatureFlag.MapsModuleFixes, false);
    const [geocodeError, setGeocodeError] = useState<string | null>(null);

    async function selected(item: any) {
      setGeocodeError(null);
      if (isMapsModuleFixEnabled) {
        try {
          const address = await buildAddress(item);
          setSearch(item);
          onChange(address);
        } catch (err) {
          datadogRum.addError(err, {
            component: 'PlacesAutocomplete',
            action: 'geocode_address',
          });
          setGeocodeError(t('web.components.placesAutocomplete.geocodeError'));
        }
      } else {
        const address = await buildAddress(item);
        setSearch(item);
        onChange(address);
      }
    }

    return (
      <div
        className={cx(styles.PlacesAutocomplete, className)}
        style={style}
        // @ts-expect-error ts-migrate(2322) FIXME: Type 'RefObject<HTMLElement>' is not assignable to... Remove this comment to see the full error message
        ref={buttonRef}
      >
        <XPlacesAutocomplete
          value={search}
          onChange={setSearch}
          onSelect={selected}
        >
          {({ getInputProps, suggestions, getSuggestionItemProps }: any) => (
            <div className={styles.search}>
              <input
                {...getInputProps({
                  placeholder,
                  className: styles.input,
                })}
                onClick={onOpen}
                disabled={disabled}
                ref={childrenRef}
                data-test="placeAutocompleteSearch"
              />
              {suggestions.length > 0 &&
                ReactDOM.createPortal(
                  <ul
                    className={styles.results}
                    // @ts-expect-error ts-migrate(2322) FIXME: Type 'RefObject<HTMLElement>' is not assignable to... Remove this comment to see the full error message
                    ref={modalRef}
                    // @ts-expect-error ts-migrate(2322) FIXME: Type '{ top: number; left: number; height?: number... Remove this comment to see the full error message
                    style={position}
                    data-test="addressDropdown"
                  >
                    {suggestions.map((suggestion: any) => (
                      <li
                        {...getSuggestionItemProps(suggestion)}
                        key={suggestion.placeId || suggestion.description}
                        data-is-active={suggestion.active}
                      >
                        <span>{suggestion.description}</span>
                      </li>
                    ))}
                  </ul>,
                  document.body
                )}
            </div>
          )}
        </XPlacesAutocomplete>
        {isMapsModuleFixEnabled && geocodeError && (
          <div className={styles.errorContainer}>
            <p className={styles.error}>{geocodeError}</p>
          </div>
        )}
      </div>
    );
  }
);

export default PlacesAutocomplete;
